import { useUserContext } from '../../../../../contexts/UserContext';
import { PromoPanel } from '../../../../../components/Toolkit/PromoPanel/PromoPanel';
import { Button } from '../../../../../components/Toolkit/Button/Button';
import { ButtonLink } from '../../../../../components/Toolkit/Button/ButtonLink';
import type { FinanceInfo } from '../../../../../components/PageWrappers/AdDetails/types';
import { GTMEvent, pushToDataLayer } from '../../../../../services/gtmService';
import { generateFinanceLink } from '../../../../../components/PageWrappers/AdDetails/helpers';
import { DFPUnit } from '../../../../../components/Toolkit/DFP/DFP';
import { AD_DETAILS_DFP_CONFIG } from '../../../../../configs/dfpConfig';
import getConfig from 'next/config';
import { ReportInfo } from '../../../../../api/types/greenlightTypes';
import { useFreeHistoryCheckModal } from '../../../../FreeHistoryCheck/FreeHistoryCheckModal/FreeHistoryCheckModal.hook';
import { useFreeHistoryCheckAnalytics } from '../../../../FreeHistoryCheck/FreeHistoryCheckAnalytics.hook';

export interface PromoPanelCollectionProps {
  breadcrumbSectionName?: string | null;
  sectionName?: string;
  cdnUrl: string;
  showGreenlight?: boolean;
  className?: string;
  financeInfo: FinanceInfo | null;
  reportInfo?: ReportInfo;
  isFreeHistoryCheckReport?: boolean;
  gtmDataTargetingProps?: GTMEvent;
}

function PromoPanelCollection({
  breadcrumbSectionName,
  sectionName,
  cdnUrl,
  showGreenlight,
  className,
  financeInfo,
  reportInfo,
  isFreeHistoryCheckReport,
  gtmDataTargetingProps,
}: PromoPanelCollectionProps) {
  const {
    publicRuntimeConfig: {
      FINANCE_PROVIDERS_DFP_SLOTS_ENABLED,
      FEATURE_FREE_HISTORY_CHECK,
    },
  } = getConfig();
  const { setIsHistoryCheckModalOpen } = useUserContext();
  const hasFinanceInfo = Boolean(financeInfo);
  const isHistoryCheck = Boolean(!hasFinanceInfo && showGreenlight);
  const showFreeHistoryCheck = Boolean(
    FEATURE_FREE_HISTORY_CHECK && isFreeHistoryCheckReport && !hasFinanceInfo,
  );
  const { open: openFreeHistoryCheckModal } = useFreeHistoryCheckModal();
  const { trackAdFreeHistoryCheckIntent } = useFreeHistoryCheckAnalytics();

  if (hasFinanceInfo) {
    className = `${className} ${financeInfo?.partnerName}-${financeInfo?.partnerType}-imp`;
  }

  if (isHistoryCheck) {
    className = `${className} donedeal-historycheck-imp`;
  }

  const handlePanelClick = () => {
    if (hasFinanceInfo) {
      pushToDataLayer({
        event: 'partner_clicks',
        servicePartnerType: financeInfo?.partnerType,
        servicePartnerName: financeInfo?.partnerName,
      });
    }

    if (isHistoryCheck) {
      pushToDataLayer({
        event: 'partner_clicks',
        servicePartnerType: 'historycheck',
        servicePartnerName: 'greenlight',
      });
    }
  };

  const showPromoPanelDFPUnit =
    FINANCE_PROVIDERS_DFP_SLOTS_ENABLED === 'true' &&
    !hasFinanceInfo &&
    !isHistoryCheck;

  return (
    <div className={className} onClick={handlePanelClick}>
      {hasFinanceInfo && (
        <PromoPanel
          title="Finance your vehicle"
          listItems={['Get approved for finance on this car']}
          illustration={`${cdnUrl}/images/illustrations/finance.svg`}
          renderButton={
            <ButtonLink
              href={generateFinanceLink(financeInfo!)}
              data-tracking="get-finance-approval"
              target="_blank"
              rel="noopener nofollow"
              ofType="TERTIARY"
            >
              {financeInfo?.buttonText}
            </ButtonLink>
          }
        />
      )}
      {showFreeHistoryCheck && (
        <PromoPanel
          title="FREE History Check on this vehicle"
          listItems={['Independently verified with official data']}
          renderButton={
            <Button
              ofType="TERTIARY"
              onClick={() => {
                openFreeHistoryCheckModal('side_panel');
                trackAdFreeHistoryCheckIntent(gtmDataTargetingProps);
              }}
              data-testid="side-panel-free-history-check-button"
            >
              {reportInfo?.buttonText}
            </Button>
          }
        />
      )}

      {isHistoryCheck && !showFreeHistoryCheck && (
        <PromoPanel
          title="Greenlight History Check"
          listItems={['Get Peace of Mind']}
          illustration={`${cdnUrl}/images/illustrations/greenlight.svg`}
          renderButton={
            <Button
              ofType="TERTIARY"
              onClick={() => setIsHistoryCheckModalOpen(true)}
            >
              Purchase History Check Report
            </Button>
          }
        />
      )}
      {breadcrumbSectionName === 'animals' && (
        <PromoPanel
          title="DoneDeal is an IPAAG Member"
          listItems={['Irish Pet Advertising Advisory Group']}
          illustration={`${cdnUrl}/images/illustrations/IPAAG.svg`}
          renderButton={
            <ButtonLink
              href={
                sectionName === 'reptiles'
                  ? 'https://ipaag.ie/buying-a-pet-advice/exotic-pets/?utm_source=DoneDeal&utm_medium=BANNER&utm_campaign=Reptiles_June_2018&utm_term=MAY_18&utm_content=REPTILES'
                  : 'https://ipaag.ie/?utm_source=DoneDeal&utm_medium=BANNER&utm_campaign=MAY_2018&utm_term=MAY_18&utm_content=IPAAG_Logo_DoneDeal'
              }
              target="_blank"
              rel="noopener"
            >
              Learn More
            </ButtonLink>
          }
        />
      )}
      {showPromoPanelDFPUnit && (
        <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_promo_panel} />
      )}
    </div>
  );
}

export { PromoPanelCollection };
